import React from "react";
import { fireEvent, render, screen } from "@testing-library/react";
import "@testing-library/jest-dom";
import { CssVarsProvider } from "@mui/joy/styles";
import ViewGridNew from "./ViewGridNew";
import { createViewService } from "../../services/viewService";

let mockGridProps: any;
jest.mock("../../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => jest.fn(),
}));
jest.mock("../../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "workspace-1" } }),
}));
jest.mock("./AuditLogPanel", () => () => null);
jest.mock("./CsvImportDialog", () => () => null);
jest.mock("../pickers/FkSelectionModal", () => () => null);
jest.mock("./ExportButton", () => () => null);
jest.mock("../../hooks/useCanCreateRow", () => ({
  useCanCreateRow: () => ({ data: null, isLoading: false }),
}));
jest.mock("../../hooks/useViewDataQuery", () => ({
  useBulkDeleteMutation: () => ({ isPending: false }),
  useImportCsvMutation: () => ({ isPending: false }),
}));
jest.mock("./useRowOperations", () => ({
  useRowOperations: ({ data }: any) => ({
    rowsWithNew: data.rows,
    newRowData: null,
  }),
}));
jest.mock("@mui/x-data-grid", () => ({
  ...jest.requireActual("@mui/x-data-grid"),
  DataGrid: (props: any) => {
    mockGridProps = props;
    return (
      <div>
        {props.rows.map((row: any) => (
          <div key={props.getRowId(row)}>
            <span>{row.name}</span>
            <button
              disabled={
                !props.isCellEditable({
                  row,
                  field: "name",
                  colDef: { editable: true },
                })
              }
              onClick={() =>
                props.processRowUpdate({ ...row, name: "Changed" }, row)
              }
            >
              Edit {row.name}
            </button>
            {props.columns
              .find((col: any) => col.field === "_actions")
              ?.renderCell({ row })}
          </div>
        ))}
      </div>
    );
  },
}));

it("keeps identity-less nested View rows visible without edit, delete or selection writes", async () => {
  const service = createViewService(
    jest.fn().mockResolvedValue({
      columns: [{ name: "name", type: "TEXT", editable: true }],
      rows: [{ name: "Anonymous" }, { _rowId: "real-row", name: "Identified" }],
      total_count: 2,
    }),
    "workspace-1",
  );
  const data = await service.queryViewData("child-view", {
    canvasId: "child-canvas",
    page: 2,
  });
  expect(data.rows[0]._rowId).toBeUndefined();
  const onUpdate = jest.fn().mockResolvedValue(undefined);
  const onDelete = jest.fn();
  const onSelect = jest.fn();
  render(
    <CssVarsProvider>
      <ViewGridNew
        view={{ id: "child-view", name: "Child" } as any}
        data={data}
        page={1}
        pageSize={25}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        onRowUpdate={onUpdate}
        onRowDelete={onDelete}
        onRowSelect={onSelect}
        allowBulkActions
      />
    </CssVarsProvider>,
  );
  expect(screen.getByText("Anonymous")).toBeInTheDocument();
  expect(screen.getByRole("button", { name: "Edit Anonymous" })).toBeDisabled();
  expect(screen.getAllByTitle("Delete")).toHaveLength(1);
  expect(mockGridProps.isRowSelectable({ row: data.rows[0] })).toBe(false);
  await mockGridProps.processRowUpdate(
    { ...data.rows[0], name: "No write" },
    data.rows[0],
  );
  mockGridProps.onRowClick({
    id: mockGridProps.getRowId(data.rows[0]),
    row: data.rows[0],
  });
  expect(onUpdate).not.toHaveBeenCalled();
  expect(onDelete).not.toHaveBeenCalled();
  expect(onSelect).not.toHaveBeenCalled();
  fireEvent.click(screen.getByRole("button", { name: "Edit Identified" }));
  expect(
    await screen.findByText("Record updated successfully"),
  ).toBeInTheDocument();
  expect(onUpdate).toHaveBeenCalledWith(
    "real-row",
    expect.objectContaining({ name: "Changed" }),
  );
});

it("preserves real integer zero and alternate identities without assigning page identities", async () => {
  const service = createViewService(
    jest.fn().mockResolvedValue({
      columns: [],
      rows: [
        { id: 0 },
        { __row_id: "uuid-row" },
        { id: "" },
        { id: { bad: true } },
      ],
    }),
    "workspace-1",
  );
  const data = await service.queryViewData("child-view", { page: 250 });
  expect(data.rows.map((row) => row._rowId)).toEqual([
    "0",
    "uuid-row",
    undefined,
    undefined,
  ]);
});
